// Healthcare Training Dataset
export const healthcareTrainingData = {
  // Symptoms and Conditions
  symptoms: {
    fever: {
      patterns: [
        "fever", "high temperature", "feeling hot", "temperature", "feverish", "burning up", "chills",
        "my child has a fever", "what to do for fever", "how to reduce fever", "is fever dangerous", "persistent fever", "fever at night"
      ],
      responses: [
        "Fever can be a sign of infection. If your temperature is above 101°F (38.3°C), consider seeing a healthcare provider. In the meantime, rest, stay hydrated, and you may take acetaminophen or ibuprofen as directed.",
        "For fever management: Rest plenty, drink lots of fluids, dress lightly, and monitor your temperature. Seek medical attention if fever persists for more than 3 days or is accompanied by severe symptoms.",
        "If you or your child has a fever, monitor for other symptoms like rash, difficulty breathing, or persistent vomiting. These may require urgent care."
      ],
      severity: "moderate",
      recommendations: ["Rest", "Hydration", "Monitor temperature", "Consider fever reducers"]
    },
    headache: {
      patterns: [
        "headache", "head pain", "migraine", "head hurts", "head ache", "throbbing head", "tension headache", "cluster headache",
        "pain in my head", "sharp headache", "headache every day", "headache after eating"
      ],
      responses: [
        "Headaches can have various causes. Try resting in a quiet, dark room, staying hydrated, and applying a cold or warm compress. If headaches are severe or persistent, consult a healthcare provider.",
        "For headache relief: Ensure adequate hydration, get proper sleep, manage stress, and consider over-the-counter pain relievers if appropriate. Persistent or severe headaches warrant medical evaluation.",
        "If your headache is sudden and severe, or accompanied by vision changes, weakness, or confusion, seek medical attention immediately."
      ],
      severity: "mild",
      recommendations: ["Rest", "Hydration", "Dark room", "Stress management"]
    },
    cough: {
      patterns: ["cough", "coughing", "throat irritation", "hacking", "persistent cough", "dry cough", "wet cough", "whooping cough"],
      responses: [
        "Coughs can be due to various causes including viral infections, allergies, or irritants. Stay hydrated, use honey for soothing, and consider a humidifier. See a doctor if cough persists or is accompanied by fever.",
        "For cough management: Stay hydrated, use throat lozenges, avoid irritants, and rest your voice. If cough produces blood or persists for more than 2 weeks, seek medical attention."
      ],
      severity: "mild",
      recommendations: ["Hydration", "Honey", "Humidifier", "Avoid irritants"]
    },
    sore_throat: {
      patterns: [
        "sore throat", "throat pain", "scratchy throat", "pain when swallowing", "throat hurts", "dry throat", "irritated throat"
      ],
      responses: [
        "A sore throat is often caused by viral infections. Gargle with warm salt water, stay hydrated, and use throat lozenges. If you have a high fever, difficulty swallowing, or white patches in your throat, see a doctor.",
        "For sore throat relief: Drink warm fluids, avoid irritants, and rest your voice. Seek medical attention if symptoms persist more than a week."
      ],
      severity: "mild",
      recommendations: ["Warm fluids", "Salt water gargle", "Rest", "Avoid irritants"]
    },
    rash: {
      patterns: [
        "rash", "skin rash", "red spots", "itchy skin", "hives", "skin irritation", "bumps on skin", "skin breakout"
      ],
      responses: [
        "Rashes can have many causes, including allergies, infections, or irritants. Try to identify and avoid triggers. Use gentle skin care and avoid scratching. If the rash is spreading rapidly, blistering, or accompanied by fever, seek medical care.",
        "For mild rashes: Keep the area clean and dry, use fragrance-free moisturizers, and avoid known irritants."
      ],
      severity: "mild",
      recommendations: ["Gentle skin care", "Avoid scratching", "Moisturize", "Monitor for worsening"]
    },
    runny_nose: {
      patterns: [
        "runny nose", "nasal discharge", "dripping nose", "clear mucus", "stuffy nose", "blocked nose"
      ],
      responses: [
        "A runny nose is often caused by colds, allergies, or irritants. Use tissues, stay hydrated, and consider antihistamines for allergies. If symptoms persist or are accompanied by high fever, see a doctor.",
        "For runny nose relief: Use saline nasal spray, avoid irritants, and rest."
      ],
      severity: "mild",
      recommendations: ["Tissues", "Hydration", "Saline spray", "Rest"]
    },
    diarrhea: {
      patterns: [
        "diarrhea", "loose stools", "frequent bowel movements", "watery stool", "stomach upset", "bowel urgency"
      ],
      responses: [
        "Diarrhea can be caused by infections, food intolerance, or medications. Stay hydrated, eat bland foods, and avoid dairy or fatty foods. Seek medical attention if diarrhea is severe, bloody, or lasts more than 2 days.",
        "For diarrhea: Drink oral rehydration solutions, rest, and monitor for signs of dehydration."
      ],
      severity: "moderate",
      recommendations: ["Hydration", "Bland diet", "Rest", "Monitor for dehydration"]
    },
    chest_pain: {
      patterns: ["chest pain", "chest hurts", "pain in chest", "chest discomfort", "heart pain", "tight chest", "burning chest"],
      responses: [
        "⚠️ Chest pain can be serious. If you're experiencing severe chest pain, shortness of breath, or pain radiating to arm/jaw, seek emergency medical attention immediately. Call 911 if symptoms are severe."
      ],
      severity: "high",
      recommendations: ["Seek immediate medical attention", "Call emergency services if severe"]
    },
    stomach_pain: {
      patterns: ["stomach pain", "abdominal pain", "belly pain", "stomach ache", "stomach hurts", "cramps", "nausea", "bloating"],
      responses: [
        "Stomach pain can have many causes. Try eating bland foods, staying hydrated, and avoiding irritants. If pain is severe, persistent, or accompanied by vomiting/fever, consult a healthcare provider.",
        "For mild stomach discomfort: Eat light, avoid spicy foods, stay hydrated, and rest. Severe or persistent abdominal pain requires medical evaluation."
      ],
      severity: "moderate",
      recommendations: ["Bland diet", "Hydration", "Rest", "Avoid irritants"]
    },
    back_pain: {
      patterns: ["back pain", "backache", "lower back pain", "upper back pain", "spine pain", "back hurts"],
      responses: [
        "Back pain is common and can result from muscle strain, poor posture, or injury. Try gentle stretching, heat/cold therapy, and over-the-counter pain relievers. See a doctor if pain persists or worsens.",
        "For back pain relief: Apply ice for acute injuries, heat for muscle tension, gentle movement, proper posture, and avoid bed rest for extended periods."
      ],
      severity: "moderate",
      recommendations: ["Heat/cold therapy", "Gentle stretching", "Proper posture", "Gradual movement"]
    },
    joint_pain: {
      patterns: ["joint pain", "arthritis", "knee pain", "shoulder pain", "elbow pain", "wrist pain", "stiff joints"],
      responses: [
        "Joint pain can be caused by arthritis, injury, or inflammation. Try gentle exercise, anti-inflammatory medications, and heat/cold therapy. Persistent joint pain should be evaluated by a healthcare provider.",
        "For joint pain management: Stay active with low-impact exercises, maintain healthy weight, use proper ergonomics, and consider physical therapy if recommended."
      ],
      severity: "moderate",
      recommendations: ["Gentle exercise", "Weight management", "Anti-inflammatory care", "Physical therapy"]
    },
    fatigue: {
      patterns: ["fatigue", "tired", "exhausted", "low energy", "weakness", "feeling drained", "sleepy"],
      responses: [
        "Fatigue can result from poor sleep, stress, medical conditions, or lifestyle factors. Ensure adequate sleep, regular exercise, balanced nutrition, and stress management. Persistent fatigue warrants medical evaluation.",
        "For fatigue management: Maintain regular sleep schedule, stay hydrated, eat nutritious meals, exercise regularly, and manage stress effectively."
      ],
      severity: "mild",
      recommendations: ["Quality sleep", "Regular exercise", "Balanced nutrition", "Stress management"]
    },
    dizziness: {
      patterns: ["dizziness", "dizzy", "lightheaded", "vertigo", "balance problems", "spinning sensation"],
      responses: [
        "Dizziness can have various causes including dehydration, blood pressure changes, or inner ear problems. Sit or lie down when dizzy, stay hydrated, and avoid sudden movements. Seek medical attention if severe or persistent.",
        "For dizziness management: Move slowly when changing positions, stay hydrated, avoid alcohol, and sit down immediately when feeling dizzy."
      ],
      severity: "moderate",
      recommendations: ["Slow movements", "Hydration", "Rest when needed", "Avoid sudden position changes"]
    },
    shortness_of_breath: {
      patterns: ["shortness of breath", "trouble breathing", "breathless", "difficulty breathing", "cannot breathe", "winded"],
      responses: [
        "⚠️ Shortness of breath can be serious. If severe or accompanied by chest pain, seek immediate medical attention. Mild breathlessness may be due to exercise, anxiety, or minor illness."
      ],
      severity: "high",
      recommendations: ["Seek medical attention if severe", "Rest in comfortable position", "Calm breathing techniques"]
    },
    allergy: {
      patterns: ["allergy", "allergic reaction", "sneezing", "itchy eyes", "runny nose", "hives", "swelling from allergy", "pollen allergy", "food allergy"],
      responses: [
        "Allergies can cause sneezing, runny nose, itchy eyes, and skin rashes. Avoid known triggers and consider antihistamines. Seek emergency care if you have trouble breathing or swelling of the face/throat.",
        "For mild allergies: Avoid triggers, use antihistamines, and monitor symptoms. Severe reactions (anaphylaxis) require immediate emergency care."
      ],
      severity: "mild",
      recommendations: ["Avoid triggers", "Antihistamines", "Monitor for severe symptoms"]
    },
    burn: {
      patterns: ["burn", "burned", "scald", "fire injury", "hot water burn", "skin burn"],
      responses: [
        "For minor burns, cool the area with running water for 10-20 minutes, cover with a clean cloth, and avoid popping blisters. For severe burns, large areas, or burns to the face/genitals, seek emergency care.",
        "If a burn is blistering, very painful, or covers a large area, seek medical attention."
      ],
      severity: "moderate",
      recommendations: ["Cool water", "Cover burn", "Seek care if severe"]
    },
    dehydration: {
      patterns: ["dehydration", "dehydrated", "not enough water", "dry mouth", "thirsty all the time", "signs of dehydration"],
      responses: [
        "Dehydration can cause dry mouth, dizziness, and dark urine. Drink water regularly, especially in hot weather or when ill. Severe dehydration may require medical attention.",
        "To prevent dehydration, drink fluids throughout the day and monitor for symptoms like dry mouth, fatigue, and reduced urination."
      ],
      severity: "moderate",
      recommendations: ["Drink water", "Monitor urine color", "Seek care if severe"]
    },
    wound: {
      patterns: ["wound", "cut", "laceration", "bleeding", "open wound", "scrape", "abrasion"],
      responses: [
        "For minor wounds, clean with water, apply pressure to stop bleeding, and cover with a clean bandage. Seek care for deep, dirty, or non-healing wounds.",
        "If bleeding is severe or won't stop, apply firm pressure and seek emergency care."
      ],
      severity: "moderate",
      recommendations: ["Clean wound", "Apply pressure", "Bandage", "Seek care if severe"]
    },
    fainting: {
      patterns: ["fainting", "passed out", "lost consciousness", "blackout", "syncope"],
      responses: [
        "Fainting can be caused by dehydration, low blood pressure, or standing up too quickly. If someone faints, lay them down and elevate their legs. Seek care if they do not wake quickly or have chest pain.",
        "If fainting is recurrent or associated with other symptoms, consult a healthcare provider."
      ],
      severity: "moderate",
      recommendations: ["Lay down", "Elevate legs", "Monitor", "Seek care if not waking"]
    },
    seizure: {
      patterns: ["seizure", "convulsion", "fit", "shaking", "epilepsy", "lost control"],
      responses: [
        "If someone is having a seizure, protect them from injury, do not put anything in their mouth, and turn them on their side. Call emergency services if the seizure lasts more than 5 minutes or they do not recover quickly.",
        "For known epilepsy, follow the person's seizure action plan. Seek emergency care for first-time or prolonged seizures."
      ],
      severity: "high",
      recommendations: ["Protect from injury", "Turn on side", "Call emergency services if needed"]
    },
    pregnancy: {
      patterns: ["pregnancy", "pregnant", "expecting", "prenatal care", "pregnancy symptoms", "morning sickness"],
      responses: [
        "Pregnancy care includes regular checkups, prenatal vitamins, healthy diet, and avoiding harmful substances. If you have severe abdominal pain, heavy bleeding, or severe headache, seek medical care immediately.",
        "Common pregnancy symptoms include nausea, fatigue, and breast tenderness. Consult your doctor for personalized advice."
      ],
      severity: "moderate",
      recommendations: ["Prenatal care", "Healthy diet", "Avoid harmful substances", "Regular checkups"]
    },
    child_health: {
      patterns: ["child health", "my child is sick", "child fever", "child cough", "child vomiting", "child rash"],
      responses: [
        "Children's health concerns often include fever, cough, and rashes. Monitor for dehydration, persistent vomiting, or difficulty breathing. Seek care for high fever, lethargy, or trouble breathing.",
        "For mild symptoms, ensure hydration, rest, and monitor closely. Seek medical attention for severe or persistent symptoms."
      ],
      severity: "moderate",
      recommendations: ["Monitor symptoms", "Hydration", "Rest", "Seek care if severe"]
    },
    elderly_care: {
      patterns: ["elderly care", "older adult health", "senior health", "falls in elderly", "memory loss in elderly", "elderly confusion"],
      responses: [
        "Elderly individuals are at higher risk for falls, confusion, and infections. Ensure safe environment, regular checkups, and monitor for sudden changes in health.",
        "For memory loss or confusion, consult a healthcare provider to rule out treatable causes."
      ],
      severity: "moderate",
      recommendations: ["Safe environment", "Regular checkups", "Monitor for changes", "Consult provider"]
    }
  },

  // General Health Information
  generalHealth: {
    nutrition: {
      patterns: ["nutrition", "diet", "healthy eating", "vitamins", "supplements", "balanced diet", "what should I eat", "nutrition tips"],
      responses: [
        "A balanced diet includes fruits, vegetables, whole grains, lean proteins, and healthy fats. Aim for variety and moderation. Consult a nutritionist for personalized dietary advice.",
        "Good nutrition basics: 5-9 servings of fruits/vegetables daily, whole grains, lean proteins, limit processed foods, stay hydrated with water, and maintain portion control.",
        "For better nutrition, reduce sugary drinks, eat more fiber, and include healthy fats like olive oil and nuts."
      ]
    },
    exercise: {
      patterns: ["exercise", "workout", "fitness", "physical activity", "sports", "how to get fit", "best exercise", "exercise tips"],
      responses: [
        "Regular exercise is crucial for health. Aim for 150 minutes of moderate activity weekly. Start slowly and gradually increase intensity. Consult your doctor before starting new exercise programs.",
        "Exercise benefits include improved cardiovascular health, stronger bones, better mood, and weight management. Choose activities you enjoy for better adherence.",
        "Try to include both aerobic and strength training exercises in your routine for overall health."
      ]
    },
    sleep: {
      patterns: ["sleep", "insomnia", "sleeping", "rest", "tired", "fatigue", "how to sleep better", "sleep tips"],
      responses: [
        "Quality sleep is essential for health. Aim for 7-9 hours nightly. Maintain a regular sleep schedule, create a comfortable environment, and avoid screens before bedtime.",
        "Sleep hygiene tips: Keep a consistent schedule, avoid caffeine late in day, create a dark and cool environment, and establish a relaxing bedtime routine.",
        "If you have trouble sleeping, try relaxation techniques before bed and limit naps during the day."
      ]
    },
    stress: {
      patterns: ["stress", "anxiety", "worried", "overwhelmed", "mental health", "how to relax", "stress relief", "reduce stress"],
      responses: [
        "Managing stress is important for overall health. Try relaxation techniques, regular exercise, adequate sleep, and social support. Consider professional help if stress becomes overwhelming.",
        "Stress management techniques: Deep breathing, meditation, regular exercise, time management, social connections, and seeking professional support when needed.",
        "For stress relief, try mindfulness, journaling, or talking to a trusted friend or counselor."
      ]
    },
    hydration: {
      patterns: ["hydration", "drink water", "dehydrated", "how much water", "fluid intake", "water needs"],
      responses: [
        "Staying hydrated is important for health. Most adults need 6-8 cups of water daily, more if active or in hot weather. Drink water regularly and monitor for signs of dehydration.",
        "For good hydration, drink water throughout the day and increase intake during exercise or illness."
      ]
    },
    hygiene: {
      patterns: ["hygiene", "hand washing", "cleanliness", "personal hygiene", "how to stay clean", "dental hygiene"],
      responses: [
        "Good hygiene helps prevent illness. Wash hands regularly, bathe daily, and keep wounds clean. Practice good dental hygiene by brushing and flossing daily.",
        "For better hygiene, wash hands before eating, after using the restroom, and after coughing or sneezing."
      ]
    }
  },

  // Medications and Treatments
  medications: {
    patterns: ["medication", "medicine", "drugs", "prescription", "pills", "treatment"],
    responses: [
      "⚠️ I cannot provide specific medical advice about medications. Always consult with a healthcare provider or pharmacist for medication questions, dosages, and interactions.",
      "For medication questions, always speak with your doctor or pharmacist. They can provide guidance on dosages, side effects, interactions, and proper usage."
    ]
  },

  // Emergency Situations
  emergencies: {
    patterns: [
      "emergency", "urgent", "severe pain", "can't breathe", "chest pain", "stroke", "heart attack", "severe bleeding", "unconscious", "seizure", "not breathing", "major accident", "loss of vision", "sudden paralysis"
    ],
    responses: [
      "🚨 This sounds like a medical emergency. Please call 911 or go to your nearest emergency room immediately. Don't delay seeking professional medical help.",
      "If you or someone else is experiencing severe symptoms such as chest pain, difficulty breathing, severe bleeding, or sudden loss of consciousness, seek emergency care immediately."
    ]
  },

  // Preventive Care
  preventiveCare: {
    patterns: ["prevention", "checkup", "screening", "vaccine", "immunization", "health maintenance", "wellness visit", "prevent illness", "health tips", "healthy habits", "lifestyle change", "diet", "exercise", "quit smoking", "reduce alcohol", "weight management", "stress reduction", "mental health"],
    responses: [
      "Preventive care is key to maintaining good health. Regular checkups, screenings, vaccinations, and healthy lifestyle choices can prevent many health issues.",
      "Important preventive measures: Annual checkups, age-appropriate screenings (mammograms, colonoscopies, etc.), staying up-to-date with vaccinations, and maintaining healthy habits.",
      "Healthy habits include regular exercise, balanced diet, adequate sleep, stress management, and avoiding tobacco and excessive alcohol.",
      "Mental health is as important as physical health. Practice mindfulness, seek support when needed, and maintain social connections."
    ]
  },

  // Additional Symptoms
  fatigue: {
    patterns: ["fatigue", "tired", "exhausted", "low energy", "worn out", "chronic fatigue", "always tired", "no energy"],
    responses: [
      "Fatigue can result from lack of sleep, stress, poor nutrition, or underlying health conditions. Ensure adequate rest, eat a balanced diet, and manage stress. Persistent fatigue should be evaluated by a healthcare provider.",
      "If you feel tired all the time, consider your sleep habits, activity level, and emotional health. If fatigue is severe or lasts more than 2 weeks, consult a doctor."
    ],
    severity: "mild",
    recommendations: ["Rest", "Balanced diet", "Sleep hygiene", "Stress management"]
  },
  nausea: {
    patterns: ["nausea", "feel sick", "queasy", "vomit", "throwing up", "upset stomach", "motion sickness"],
    responses: [
      "Nausea can be caused by infections, food poisoning, motion sickness, or other conditions. Rest, stay hydrated, and eat bland foods. Seek medical attention if vomiting is severe or persistent.",
      "If nausea is accompanied by severe pain, blood in vomit, or signs of dehydration, seek medical care immediately."
    ],
    severity: "mild",
    recommendations: ["Hydration", "Rest", "Bland diet", "Medical evaluation if severe"]
  },
  rash: {
    patterns: ["rash", "skin spots", "redness", "itchy skin", "hives", "skin irritation", "bumps on skin", "skin allergy"],
    responses: [
      "Rashes can be caused by allergies, infections, or irritants. Avoid triggers, use gentle skin care, and consider antihistamines for itching. Seek medical attention for severe, spreading, or painful rashes.",
      "If rash is accompanied by fever, difficulty breathing, or swelling, seek emergency care."
    ],
    severity: "mild",
    recommendations: ["Avoid triggers", "Gentle skin care", "Antihistamines", "Medical evaluation if severe"]
  },
  anxiety: {
    patterns: ["anxiety", "nervous", "worried", "panic", "stress", "anxious", "panic attack", "feeling tense"],
    responses: [
      "Anxiety is common and can be managed with relaxation techniques, regular exercise, and talking to someone you trust. If anxiety interferes with daily life, consider seeking professional help.",
      "Practice deep breathing, mindfulness, and maintain a healthy routine. Persistent or severe anxiety may require evaluation by a mental health professional."
    ],
    severity: "mild",
    recommendations: ["Relaxation", "Exercise", "Social support", "Professional help if needed"]
  },
  back_pain: {
    patterns: ["back pain", "lower back pain", "upper back pain", "spine pain", "back hurts", "stiff back", "back injury"],
    responses: [
      "Back pain can result from muscle strain, poor posture, or injury. Rest, gentle stretching, and over-the-counter pain relief may help. Seek medical attention for severe, persistent, or radiating pain.",
      "Maintain good posture, exercise regularly, and avoid heavy lifting. If back pain is accompanied by numbness, weakness, or loss of bladder control, seek emergency care."
    ],
    severity: "moderate",
    recommendations: ["Rest", "Stretching", "Pain relief", "Medical evaluation if severe"]
  },
  allergies: {
    patterns: ["allergy", "allergies", "sneezing", "runny nose", "itchy eyes", "hay fever", "seasonal allergies", "allergic reaction"],
    responses: [
      "Allergies can cause sneezing, runny nose, and itchy eyes. Avoid known triggers, use antihistamines, and keep windows closed during high pollen seasons.",
      "For severe allergic reactions (anaphylaxis), seek emergency care immediately."
    ],
    severity: "mild",
    recommendations: ["Avoid triggers", "Antihistamines", "Medical evaluation if severe"]
  },
  dehydration: {
    patterns: ["dehydration", "dry mouth", "thirsty", "not drinking enough", "lost fluids", "heat exhaustion"],
    responses: [
      "Dehydration can cause fatigue, dizziness, and confusion. Drink plenty of water, especially in hot weather or during illness. Seek medical attention for severe dehydration or confusion.",
      "Signs of dehydration: dry mouth, dark urine, low energy. Drink fluids regularly and avoid excessive caffeine or alcohol."
    ],
    severity: "moderate",
    recommendations: ["Hydration", "Monitor urine color", "Medical evaluation if severe"]
  },
  insomnia: {
    patterns: ["insomnia", "can't sleep", "trouble sleeping", "wake up often", "poor sleep", "sleep disorder"],
    responses: [
      "Insomnia can be managed by maintaining a regular sleep schedule, avoiding screens before bed, and practicing relaxation techniques. Persistent sleep problems may require medical evaluation.",
      "Good sleep hygiene includes a consistent bedtime, limiting caffeine, and creating a comfortable sleep environment."
    ],
    severity: "mild",
    recommendations: ["Sleep hygiene", "Relaxation", "Limit caffeine", "Medical evaluation if severe"]
  },
  constipation: {
    patterns: ["constipation", "hard stool", "can't poop", "infrequent bowel movement", "straining", "blocked up"],
    responses: [
      "Constipation can be relieved by increasing fiber intake, drinking more water, and regular exercise. If constipation is severe or lasts more than a week, consult a healthcare provider.",
      "Avoid excessive use of laxatives. Seek medical attention for severe pain, blood in stool, or sudden changes in bowel habits."
    ],
    severity: "mild",
    recommendations: ["Increase fiber", "Hydration", "Exercise", "Medical evaluation if severe"]
  },
  diarrhea: {
    patterns: ["diarrhea", "loose stool", "watery stool", "frequent bowel movement", "stomach upset", "food poisoning"],
    responses: [
      "Diarrhea can be caused by infections, food intolerance, or medications. Stay hydrated, eat bland foods, and avoid dairy. Seek medical attention for blood in stool, dehydration, or diarrhea lasting more than 2 days.",
      "If diarrhea is accompanied by severe pain, fever, or vomiting, consult a doctor."
    ],
    severity: "mild",
    recommendations: ["Hydration", "Bland diet", "Medical evaluation if severe"]
  },
  dizziness: {
    patterns: ["dizzy", "lightheaded", "vertigo", "spinning", "faint", "feel faint", "unsteady"],
    responses: [
      "Dizziness can be caused by dehydration, low blood pressure, or inner ear problems. Sit or lie down, drink fluids, and avoid sudden movements. Seek medical attention for persistent or severe dizziness.",
      "If dizziness is accompanied by chest pain, shortness of breath, or confusion, seek emergency care."
    ],
    severity: "mild",
    recommendations: ["Hydration", "Rest", "Medical evaluation if severe"]
  },
  swelling: {
    patterns: ["swelling", "edema", "puffy", "swollen", "fluid retention", "swollen ankles", "swollen feet"],
    responses: [
      "Swelling can be caused by injury, infection, or medical conditions. Elevate the affected area, apply ice, and rest. Seek medical attention for sudden, severe, or unexplained swelling.",
      "If swelling is accompanied by pain, redness, or difficulty breathing, seek emergency care."
    ],
    severity: "moderate",
    recommendations: ["Elevation", "Ice", "Rest", "Medical evaluation if severe"]
  },
  vision_changes: {
    patterns: ["vision changes", "blurry vision", "double vision", "loss of vision", "eye pain", "see spots", "sudden blindness"],
    responses: [
      "Vision changes can be caused by eye strain, injury, or medical conditions. Rest your eyes, avoid screens, and seek medical attention for sudden or severe changes.",
      "If vision loss is sudden or accompanied by pain, seek emergency care immediately."
    ],
    severity: "moderate",
    recommendations: ["Rest eyes", "Avoid screens", "Medical evaluation if severe"]
  },
  chest_pain: {
    patterns: ["chest pain", "tight chest", "pressure in chest", "heart pain", "chest discomfort", "pain in chest"],
    responses: [
      "Chest pain can be a sign of a serious condition. If pain is severe, persistent, or accompanied by shortness of breath, sweating, or nausea, seek emergency care immediately.",
      "For mild chest discomfort, rest and monitor symptoms. If in doubt, seek medical attention."
    ],
    severity: "severe",
    recommendations: ["Rest", "Monitor symptoms", "Emergency care if severe"]
  },
  shortness_of_breath: {
    patterns: ["shortness of breath", "can't breathe", "difficulty breathing", "breathless", "wheezing", "tight chest"],
    responses: [
      "Shortness of breath can be caused by asthma, infections, heart problems, or anxiety. Sit upright, try to relax, and seek medical attention if symptoms are severe or sudden.",
      "If you cannot breathe or have severe difficulty, call emergency services immediately."
    ],
    severity: "severe",
    recommendations: ["Sit upright", "Relax", "Emergency care if severe"]
  },
  bleeding: {
    patterns: ["bleeding", "blood loss", "cut", "wound", "bleeding a lot", "nosebleed", "bleeding from"],
    responses: [
      "Bleeding should be stopped with direct pressure. Clean the wound and seek medical attention for severe, persistent, or unexplained bleeding.",
      "For nosebleeds, pinch the nose and lean forward. Seek emergency care for uncontrolled bleeding."
    ],
    severity: "severe",
    recommendations: ["Direct pressure", "Clean wound", "Emergency care if severe"]
  },
  unconsciousness: {
    patterns: ["unconscious", "passed out", "fainted", "not waking up", "loss of consciousness"],
    responses: [
      "Unconsciousness requires immediate medical attention. Call emergency services and check for breathing and pulse.",
      "If someone is unconscious, do not leave them alone and seek help immediately."
    ],
    severity: "severe",
    recommendations: ["Call emergency services", "Check breathing", "Do not leave alone"]
  },
  seizure: {
    patterns: ["seizure", "convulsion", "fit", "shaking", "unresponsive", "epilepsy"],
    responses: [
      "Seizures require medical evaluation. Protect the person from injury, do not restrain, and seek emergency care if it lasts more than 5 minutes or if the person does not regain consciousness.",
      "If someone has a seizure, stay calm, clear the area, and call for help if needed."
    ],
    severity: "severe",
    recommendations: ["Protect from injury", "Do not restrain", "Emergency care if severe"]
  },
  stroke: {
    patterns: ["stroke", "sudden paralysis", "face droop", "arm weakness", "speech difficulty", "FAST symptoms"],
    responses: [
      "Stroke is a medical emergency. Call emergency services immediately if you notice sudden weakness, facial droop, or speech difficulty.",
      "Remember FAST: Face drooping, Arm weakness, Speech difficulty, Time to call emergency services."
    ],
    severity: "severe",
    recommendations: ["Call emergency services", "FAST assessment", "Emergency care"]
  },
  heart_attack: {
    patterns: ["heart attack", "chest pain", "pressure in chest", "shortness of breath", "sweating", "nausea", "pain radiating"],
    responses: [
      "Heart attack is a medical emergency. Call emergency services immediately if you have chest pain, shortness of breath, or pain radiating to arm/jaw.",
      "Do not drive yourself to the hospital. Chew an aspirin if not allergic and wait for help."
    ],
    severity: "severe",
    recommendations: ["Call emergency services", "Chew aspirin if not allergic", "Emergency care"]
  },
  major_accident: {
    patterns: ["major accident", "serious injury", "trauma", "car crash", "fall from height", "severe wound"],
    responses: [
      "Major accidents require immediate medical attention. Call emergency services, provide first aid if safe, and do not move injured persons unless necessary.",
      "Ensure your own safety before helping others."
    ],
    severity: "severe",
    recommendations: ["Call emergency services", "First aid", "Do not move unless necessary"]
  },
  loss_of_vision: {
    patterns: ["loss of vision", "sudden blindness", "can't see", "vision gone", "blindness"],
    responses: [
      "Sudden loss of vision is a medical emergency. Seek immediate care.",
      "Do not wait for symptoms to improve. Call emergency services."
    ],
    severity: "severe",
    recommendations: ["Seek immediate care", "Call emergency services"]
  }
};

// Intent Recognition Patterns
export const intentPatterns = {
  greeting: ["hello", "hi", "hey", "good morning", "good afternoon", "good evening", "greetings", "howdy", "yo"],
  goodbye: ["bye", "goodbye", "see you", "thanks", "thank you", "farewell", "take care", "later"],
  help: ["help", "what can you do", "how can you help", "assistance", "support", "need help", "can you assist"],
  symptom_check: ["i have", "i feel", "experiencing", "symptoms", "pain", "hurt", "not feeling well", "my child has", "my parent has", "my friend has"],
  general_info: ["what is", "tell me about", "information", "explain", "details", "describe", "general health", "health tips", "wellness"],
  preventive_care: ["preventive care", "wellness", "healthy habits", "lifestyle", "diet", "exercise", "mental health", "stress management"],
  emergency: ["emergency", "urgent", "severe pain", "can't breathe", "chest pain", "stroke", "heart attack", "severe bleeding", "unconscious", "seizure", "not breathing", "major accident", "loss of vision", "sudden paralysis"]
};

// Default Responses
export const defaultResponses = [
  "I'm here to provide general health information. For specific medical concerns, please consult with a healthcare professional.",
  "I can help with general health questions, but for personalized medical advice, please speak with your doctor.",
  "Could you please provide more details about your health concern? I'm here to help with general health information.",
  "I understand you have a health question. Please remember that I provide general information only - for medical diagnosis or treatment, consult a healthcare provider."
];

// Disclaimer
export const medicalDisclaimer = "⚠️ Disclaimer: This chatbot provides general health information only and is not a substitute for professional medical advice, diagnosis, or treatment. Always consult with qualified healthcare providers for medical concerns.";
